import { Fragment, useEffect, useMemo, useState, type ChangeEvent } from 'react';
import {
  MapPin, Plus, Loader2, Ticket, Film, X, Download, RefreshCw, Link2,
  ChevronRight, ChevronDown, Search, Music, User, Calendar, ExternalLink, Pencil,
  CheckCircle2, Trash2, Upload, Image as ImageIcon, Type,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { supabase } from '../lib/supabase';

// New tour tables aren't in the generated Database types yet; use an untyped
// handle for those queries (regenerate types after the migration is applied).
const db = supabase as any;

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_ANON = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

// Direct edge-function call with verbose logging. Uses native fetch to bypass
// the app-wide Supabase fetch wrapper (which aborts after 5s and breaks longer
// function calls). Logs request/response so we can see exactly what happens.
async function callFn<T = any>(name: string, body: unknown): Promise<T> {
  const url = `${SUPABASE_URL}/functions/v1/${name}`;
  let token = SUPABASE_ANON;
  try {
    const { data } = await supabase.auth.getSession();
    if (data?.session?.access_token) token = data.session.access_token;
  } catch (e) {
    console.warn('[tour] getSession failed, falling back to anon key', e);
  }
  const t0 = performance.now();
  console.log(`[tour] → calling ${name}`, { url, body });
  let res: Response;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        apikey: SUPABASE_ANON,
      },
      body: JSON.stringify(body),
    });
  } catch (e) {
    console.error(`[tour] ✗ network error calling ${name}`, e);
    throw new Error(`Network error calling ${name}: ${(e as Error).message}`);
  }
  const ms = Math.round(performance.now() - t0);
  const text = await res.text();
  let json: any;
  try { json = JSON.parse(text); } catch { json = { raw: text }; }
  console.log(`[tour] ← ${name} responded ${res.status} in ${ms}ms`, json);
  if (!res.ok) throw new Error(json?.error || `HTTP ${res.status}`);
  return json as T;
}

interface Tour {
  id: string;
  name: string;
  artist_name: string;
  tour_url: string | null;
  status: string;
  songso_slug: string | null;
  dateCount?: number;
}

interface TourDate {
  id: string;
  date: string;
  city: string | null;
  venue: string | null;
  country: string | null;
  ticket_url: string | null;
  price: number | null;
  currency: string | null;
  status: 'available' | 'limited' | 'sold_out';
  budget: number | null;
  budget_spent: number | null;
  report_id: string | null;
}

interface Creative {
  id: string;
  tour_date_id: string;
  url: string;
  thumbnail_url: string | null;
  label: string | null;
  base_clip_id: string | null;
}

interface ArtistPreset {
  artist_key: string;
  label: string;
}
interface BaseClip {
  id: string;
  name: string;
  artist_key: string;
  url: string;
  width: number | null;
  height: number | null;
}

const STATUS_LABELS: Record<string, string> = {
  available: 'Available',
  limited: 'Few left',
  sold_out: 'Sold out',
};
const STATUS_COLORS: Record<string, string> = {
  available: 'bg-green-500/15 text-green-400',
  limited: 'bg-amber-500/15 text-amber-400',
  sold_out: 'bg-red-500/15 text-red-400',
};
const TOUR_STATUS_COLORS: Record<string, string> = {
  active: 'bg-green-500/15 text-green-400 border border-green-500/30',
  draft: 'bg-amber-500/15 text-amber-400 border border-amber-500/30',
  archived: 'bg-gray-600/30 text-gray-400 border border-gray-600/40',
};

export function TourDates() {
  const [tours, setTours] = useState<Tour[]>([]);
  const [selectedTourId, setSelectedTourId] = useState<string | null>(null);
  const [dates, setDates] = useState<TourDate[]>([]);
  const [creatives, setCreatives] = useState<Creative[]>([]);
  const [loading, setLoading] = useState(false);
  const [importValue, setImportValue] = useState('');
  const [importing, setImporting] = useState(false);
  const [query, setQuery] = useState('');
  const [galleryDate, setGalleryDate] = useState<TourDate | null>(null);
  const [editArtist, setEditArtist] = useState<Tour | null>(null);

  const selectedTour = useMemo(
    () => tours.find((t) => t.id === selectedTourId) || null,
    [tours, selectedTourId]
  );

  async function loadTours() {
    const { data, error } = await db
      .from('tours')
      .select('id, name, artist_name, tour_url, status, songso_slug, tour_dates(count)')
      .order('created_at', { ascending: false });
    if (error) { toast.error(`Load tours failed: ${error.message}`); return; }
    const mapped = (data || []).map((t: any) => ({
      ...t,
      dateCount: Array.isArray(t.tour_dates) ? (t.tour_dates[0]?.count ?? 0) : 0,
    }));
    setTours(mapped);
  }

  async function loadDates(tourId: string) {
    setLoading(true);
    const { data, error } = await db
      .from('tour_dates')
      .select('*')
      .eq('tour_id', tourId)
      .order('date', { ascending: true });
    if (error) toast.error(`Load dates failed: ${error.message}`);
    setDates((data as TourDate[]) || []);

    const ids = (data || []).map((d: any) => d.id);
    if (ids.length) {
      const { data: cr } = await db
        .from('tour_date_video_creatives')
        .select('id, tour_date_id, url, thumbnail_url, label, base_clip_id')
        .in('tour_date_id', ids);
      setCreatives((cr as Creative[]) || []);
    } else {
      setCreatives([]);
    }
    setLoading(false);
  }

  useEffect(() => { loadTours(); }, []);
  useEffect(() => { if (selectedTourId) loadDates(selectedTourId); }, [selectedTourId]);

  async function handleImport() {
    if (!importValue.trim()) return;
    setImporting(true);
    try {
      const data = await callFn('import-songso-tour', { link: importValue.trim() });
      if (data?.error) throw new Error(data.error);
      toast.success(`Imported "${data.tourName}" — ${data.datesImported} dates`);
      setImportValue('');
      await loadTours();
      if (data.tourId) setSelectedTourId(data.tourId);
    } catch (e: any) {
      console.error('[tour] import failed', e);
      toast.error(`Import failed: ${e.message || e}`);
    } finally {
      setImporting(false);
    }
  }

  async function patchDate(id: string, patch: Partial<TourDate>) {
    setDates((ds) => ds.map((d) => (d.id === id ? { ...d, ...patch } : d)));
    const { error } = await db.from('tour_dates').update(patch).eq('id', id);
    if (error) toast.error(`Save failed: ${error.message}`);
  }

  const creativesByDate = useMemo(() => {
    const m: Record<string, Creative[]> = {};
    for (const c of creatives) (m[c.tour_date_id] ||= []).push(c);
    return m;
  }, [creatives]);

  const filteredTours = tours.filter((t) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (t.name || '').toLowerCase().includes(q) || (t.artist_name || '').toLowerCase().includes(q);
  });

  return (
    <div className="min-h-screen bg-black p-8 text-gray-100">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-start mb-6">
          <div className="flex items-center gap-3">
            <MapPin className="text-[#0000fe]" />
            <div>
              <h1 className="text-3xl font-bold text-white">Tours</h1>
              <p className="text-gray-400">Tour dates imported from song.so</p>
            </div>
          </div>
          <button onClick={loadTours}
            className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors text-sm">
            <RefreshCw size={16} /> Refresh
          </button>
        </div>

        {/* Import bar */}
        <div className="flex gap-2 mb-4">
          <div className="relative flex-1 max-w-xl">
            <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
            <input
              value={importValue}
              onChange={(e) => setImportValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleImport()}
              placeholder="Paste a song.so tour link (e.g. https://link.nf/love-language-tour-2025)"
              className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#0000fe]"
            />
          </div>
          <button onClick={handleImport} disabled={importing}
            className="flex items-center gap-2 bg-[#0000fe] hover:brightness-110 px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50">
            {importing ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />}
            Import from song.so
          </button>
        </div>

        {/* Search */}
        <div className="mb-6 relative max-w-xl">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search tours by name or artist…"
            className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0000fe]"
          />
        </div>

        <div className="bg-gray-900 rounded-lg border border-gray-800 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-800 border-b border-gray-700">
                <tr>
                  <th className="w-10 px-3 py-3"></th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Tour</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Künstler</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Dates</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">Aktionen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {filteredTours.length === 0 && (
                  <tr><td colSpan={6} className="px-6 py-10 text-center text-gray-500">
                    {tours.length === 0 ? 'No tours yet. Import one above.' : 'No tours match your search.'}
                  </td></tr>
                )}
                {filteredTours.map((t) => {
                  const expanded = selectedTourId === t.id;
                  return (
                    <Fragment key={t.id}>
                      <tr className="hover:bg-gray-800/50 transition-colors cursor-pointer"
                          onClick={() => setSelectedTourId(expanded ? null : t.id)}>
                        <td className="px-3 py-4 text-gray-400">
                          {expanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Music className="w-4 h-4 text-gray-500 mr-2" />
                            <div>
                              <div className="text-sm font-medium text-white">{t.name}</div>
                              <div className="text-xs text-gray-500">{t.songso_slug || 'Tour'}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-gray-300">
                            <User className="w-4 h-4 mr-2 text-gray-500" />{t.artist_name}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-gray-300">
                            <Calendar className="w-4 h-4 mr-2 text-gray-500" />{t.dateCount ?? 0} dates
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium border ${TOUR_STATUS_COLORS[t.status] || 'bg-gray-500/20 text-gray-400 border-gray-500/30'}`}>{t.status}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                          <div className="flex items-center justify-end gap-2">
                            {t.tour_url && (
                              <a href={t.tour_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                                 className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded transition-colors flex items-center gap-1">
                                <ExternalLink className="w-4 h-4" />song.so
                              </a>
                            )}
                            <button onClick={(e) => { e.stopPropagation(); setEditArtist(t); }}
                              title="Artist render settings"
                              className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors flex items-center gap-1">
                              <Pencil className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                      {expanded && (
                        <tr>
                          <td colSpan={6} className="bg-black/30 px-4 py-4">

          {loading ? (
            <div className="flex items-center gap-2 text-gray-400 py-10">
              <Loader2 className="animate-spin" size={18} /> Loading…
            </div>
          ) : (
            <div className="overflow-x-auto rounded-lg ring-1 ring-white/10">
              <table className="w-full text-sm">
                <thead className="bg-[#1a1a1a] text-gray-400 text-xs uppercase">
                  <tr>
                    {['Date', 'Venue / City', 'Status', 'Price', 'Budget', 'Spent', 'Report', 'Creatives', ''].map((h) => (
                      <th key={h} className="text-left font-medium px-3 py-2.5 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dates.map((d) => {
                    const cr = creativesByDate[d.id] || [];
                    return (
                      <tr key={d.id} className="border-t border-white/5">
                        <td className="px-3 py-2.5 whitespace-nowrap">
                          {format(new Date(d.date), 'dd.MM.yyyy')}
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="font-medium">{d.venue || '—'}</div>
                          <div className="text-xs text-gray-500">
                            {d.city}{d.country ? `, ${d.country.toUpperCase()}` : ''}
                          </div>
                        </td>
                        <td className="px-3 py-2.5">
                          <select
                            value={d.status}
                            onChange={(e) => patchDate(d.id, { status: e.target.value as TourDate['status'] })}
                            className={`rounded px-2 py-1 text-xs ${STATUS_COLORS[d.status]} bg-transparent`}
                          >
                            {Object.entries(STATUS_LABELS).map(([v, l]) => (
                              <option key={v} value={v} className="bg-[#1a1a1a] text-white">{l}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-gray-300">
                          {d.price != null ? `${d.price} ${d.currency || ''}` : '—'}
                        </td>
                        <td className="px-3 py-2.5">
                          <NumCell value={d.budget} onSave={(v) => patchDate(d.id, { budget: v })} />
                        </td>
                        <td className="px-3 py-2.5">
                          <NumCell value={d.budget_spent} onSave={(v) => patchDate(d.id, { budget_spent: v })} />
                        </td>
                        <td className="px-3 py-2.5">
                          <TextCell value={d.report_id} placeholder="report id"
                            onSave={(v) => patchDate(d.id, { report_id: v })} />
                        </td>
                        <td className="px-3 py-2.5">
                          {cr.length > 0 ? (
                            <button onClick={() => setGalleryDate(d)}
                              title={`${cr.length} creative${cr.length > 1 ? 's' : ''}`}
                              className="flex items-center gap-1 text-xs font-medium text-green-400 hover:text-green-300">
                              <CheckCircle2 size={16} /> {cr.length}
                            </button>
                          ) : (
                            <span className="text-xs text-gray-600">none</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <button onClick={() => setGalleryDate(d)}
                              className="flex items-center gap-1 text-xs bg-white/5 hover:bg-white/10 px-2 py-1 rounded">
                              <Film size={12} /> Video
                            </button>
                            {d.ticket_url && (
                              <a href={d.ticket_url} target="_blank" rel="noreferrer"
                                 className="text-gray-400 hover:text-white"><Ticket size={14} /></a>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {dates.length === 0 && (
                    <tr><td colSpan={9} className="px-3 py-8 text-center text-gray-500">No tour dates.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {galleryDate && (
        <CreativesModal
          date={galleryDate}
          artistNameHint={selectedTour?.artist_name || ''}
          onClose={() => setGalleryDate(null)}
          onChanged={() => selectedTourId && loadDates(selectedTourId)}
        />
      )}

      {editArtist && (
        <ArtistAssetsModal
          artistName={editArtist.artist_name}
          onClose={() => setEditArtist(null)}
        />
      )}
    </div>
  );
}

function downloadUrl(c: Creative) {
  const sep = c.url.includes('?') ? '&' : '?';
  const name = `${(c.label || 'tour-video').replace(/[^a-z0-9]+/gi, '-')}.mp4`;
  return `${c.url}${sep}download=${encodeURIComponent(name)}`;
}

const ASSET_BUCKET = 'tour-video-assets';

function slugify(s: string) {
  return (s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}
function fileNameFromUrl(url: string | null) {
  if (!url) return '';
  try { return decodeURIComponent(url.split('?')[0].split('/').pop() || ''); } catch { return ''; }
}

interface ArtistRow {
  artist_key: string;
  label: string;
  font_url: string;
  logo_url: string | null;
  italic: boolean;
  preset: Record<string, any>;
}

function ArtistAssetsModal({ artistName, onClose }: { artistName: string; onClose: () => void }) {
  const [loading, setLoading] = useState(true);
  const [existing, setExisting] = useState<ArtistRow | null>(null);
  const [artistKey, setArtistKey] = useState('');
  const [label, setLabel] = useState('');
  const [italic, setItalic] = useState(false);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [fontUrl, setFontUrl] = useState('');
  const [clips, setClips] = useState<BaseClip[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function loadAll() {
    setLoading(true); setErr(null);
    const hint = (artistName || '').toLowerCase();
    const { data } = await db.from('tour_video_artists')
      .select('artist_key, label, font_url, logo_url, italic, preset');
    const list: ArtistRow[] = data || [];
    const match = list.find((a) =>
      a.artist_key.toLowerCase() === hint || (a.label || '').toLowerCase() === hint) || null;
    if (match) {
      setExisting(match);
      setArtistKey(match.artist_key);
      setLabel(match.label);
      setItalic(!!match.italic);
      setLogoUrl(match.logo_url);
      setFontUrl(match.font_url || '');
      const { data: cd } = await db.from('tour_video_base_clips')
        .select('id, name, artist_key, url, width, height').eq('artist_key', match.artist_key).order('sort');
      setClips((cd as BaseClip[]) || []);
    } else {
      setExisting(null);
      setArtistKey(slugify(artistName) || 'artist');
      setLabel(artistName || '');
      setItalic(false); setLogoUrl(null); setFontUrl(''); setClips([]);
    }
    setLoading(false);
  }
  useEffect(() => { loadAll(); }, [artistName]);

  async function uploadTo(path: string, file: File) {
    const { error } = await supabase.storage.from(ASSET_BUCKET)
      .upload(path, file, { upsert: true, contentType: file.type || undefined });
    if (error) throw error;
    return supabase.storage.from(ASSET_BUCKET).getPublicUrl(path).data.publicUrl;
  }

  // Make sure the artist preset row exists (FK target for base clips). Creates a
  // minimal row from the current fields when it doesn't exist yet.
  async function ensureArtist(): Promise<string> {
    const key = artistKey.trim();
    if (!key) throw new Error('Artist key is required.');
    if (existing) return key;
    const row = { artist_key: key, label: label.trim() || key, font_url: fontUrl || null, logo_url: logoUrl, italic };
    const { error } = await db.from('tour_video_artists').upsert(row, { onConflict: 'artist_key' });
    if (error) throw error;
    setExisting({ ...row, font_url: fontUrl, preset: {} } as ArtistRow);
    return key;
  }

  async function onLogoFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file) return;
    setBusy('logo'); setErr(null);
    try {
      const url = await uploadTo(`logos/${artistKey}/${Date.now()}-${file.name}`, file);
      setLogoUrl(url);
      if (existing) { await db.from('tour_video_artists').update({ logo_url: url }).eq('artist_key', artistKey); toast.success('Logo updated'); }
    } catch (e: any) { setErr(e.message || String(e)); } finally { setBusy(null); }
  }

  async function onFontFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file) return;
    setBusy('font'); setErr(null);
    try {
      const url = await uploadTo(`fonts/${artistKey}/${file.name}`, file);
      setFontUrl(url);
      if (existing) { await db.from('tour_video_artists').update({ font_url: url }).eq('artist_key', artistKey); toast.success('Font updated'); }
    } catch (e: any) { setErr(e.message || String(e)); } finally { setBusy(null); }
  }

  async function onClipFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file) return;
    if (!artistKey.trim()) { setErr('Artist key is required.'); return; }
    setBusy('clip'); setErr(null);
    try {
      const key = await ensureArtist();
      const url = await uploadTo(`clips/${key}/${Date.now()}-${file.name}`, file);
      const { data, error } = await db.from('tour_video_base_clips')
        .insert({ artist_key: key, name: file.name.replace(/\.[^.]+$/, ''), url, width: 1080, height: 1920, sort: clips.length })
        .select('id, name, artist_key, url, width, height').single();
      if (error) throw error;
      setClips((cs) => [...cs, data as BaseClip]);
      toast.success('Base video added');
    } catch (e: any) { setErr(e.message || String(e)); } finally { setBusy(null); }
  }

  async function deleteClip(clip: BaseClip) {
    if (!confirm(`Delete base video "${clip.name}"?`)) return;
    const { error } = await db.from('tour_video_base_clips').delete().eq('id', clip.id);
    if (error) { setErr(error.message); return; }
    setClips((cs) => cs.filter((c) => c.id !== clip.id));
    try {
      const path = clip.url.split(`/${ASSET_BUCKET}/`)[1];
      if (path) await supabase.storage.from(ASSET_BUCKET).remove([decodeURIComponent(path.split('?')[0])]);
    } catch { /* best effort */ }
    toast.success('Base video deleted');
  }

  async function save() {
    if (!artistKey.trim()) { setErr('Artist key is required.'); return; }
    setSaving(true); setErr(null);
    try {
      const row = {
        artist_key: artistKey.trim(), label: label.trim() || artistKey.trim(),
        font_url: fontUrl, logo_url: logoUrl, italic,
      };
      const { error } = await db.from('tour_video_artists').upsert(row, { onConflict: 'artist_key' });
      if (error) throw error;
      toast.success(existing ? 'Artist saved' : 'Artist preset created');
      await loadAll();
    } catch (e: any) { setErr(e.message || String(e)); } finally { setSaving(false); }
  }

  const fontName = fileNameFromUrl(fontUrl);

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-[#1a1a1a] p-6 ring-1 ring-white/10" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold">Artist render settings</h3>
            <p className="text-xs text-gray-500">{artistName}{existing ? '' : ' · new preset'}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={18} /></button>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-gray-400 py-10"><Loader2 className="animate-spin" size={18} /> Loading…</div>
        ) : (
          <div className="space-y-5">
            {!existing && (
              <p className="text-xs text-amber-400 bg-amber-500/10 ring-1 ring-amber-500/20 rounded-lg px-3 py-2">
                No preset exists for this artist yet. Add base videos, a logo and a font below — the preset is created automatically on your first upload. A font is required before rendering.
              </p>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-gray-400 mb-1">Label</label>
                <input value={label} onChange={(e) => setLabel(e.target.value)}
                  className="w-full bg-[#111] rounded-lg px-3 py-2 text-sm ring-1 ring-white/10" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">Artist key</label>
                <input value={artistKey} onChange={(e) => setArtistKey(slugify(e.target.value))}
                  disabled={!!existing}
                  className="w-full bg-[#111] rounded-lg px-3 py-2 text-sm ring-1 ring-white/10 disabled:opacity-50" />
              </div>
            </div>

            <label className="flex items-center gap-2 text-sm text-gray-300">
              <input type="checkbox" checked={italic} onChange={(e) => setItalic(e.target.checked)} />
              Italic date/venue text
            </label>

            {/* Logo */}
            <div>
              <label className="block text-xs text-gray-400 mb-1 flex items-center gap-1"><ImageIcon size={12} /> Logo (PNG)</label>
              <div className="flex items-center gap-3">
                <div className="w-24 h-24 rounded-lg bg-black/40 ring-1 ring-white/10 grid place-items-center overflow-hidden shrink-0">
                  {logoUrl ? <img src={logoUrl} className="w-full h-full object-contain" /> : <ImageIcon size={20} className="text-gray-600" />}
                </div>
                <label className="flex items-center gap-1.5 text-xs bg-white/5 hover:bg-white/10 px-3 py-2 rounded-lg cursor-pointer">
                  {busy === 'logo' ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />}
                  {logoUrl ? 'Replace logo' : 'Upload logo'}
                  <input type="file" accept="image/png,image/webp,image/jpeg" className="hidden" onChange={onLogoFile} disabled={!!busy} />
                </label>
              </div>
            </div>

            {/* Font */}
            <div>
              <label className="block text-xs text-gray-400 mb-1 flex items-center gap-1"><Type size={12} /> Font (.ttf / .otf)</label>
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-300 truncate">{fontName || <span className="text-gray-600">none</span>}</span>
                <label className="flex items-center gap-1.5 text-xs bg-white/5 hover:bg-white/10 px-3 py-2 rounded-lg cursor-pointer shrink-0">
                  {busy === 'font' ? <Loader2 className="animate-spin" size={14} /> : <Upload size={14} />}
                  {fontUrl ? 'Replace font' : 'Upload font'}
                  <input type="file" accept=".ttf,.otf,font/ttf,font/otf" className="hidden" onChange={onFontFile} disabled={!!busy} />
                </label>
              </div>
            </div>

            {/* Base videos */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs text-gray-400 flex items-center gap-1"><Film size={12} /> Base (blank) videos</label>
                <label className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg ${artistKey && !busy ? 'bg-[#0000fe] hover:brightness-110 cursor-pointer' : 'bg-white/5 opacity-50 cursor-not-allowed'}`}>
                  {busy === 'clip' ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />}
                  Add base video
                  <input type="file" accept="video/mp4,video/quicktime,video/webm" className="hidden" onChange={onClipFile} disabled={!artistKey || !!busy} />
                </label>
              </div>
              {clips.length === 0 ? (
                <p className="text-xs text-gray-600">No base videos yet.</p>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {clips.map((clip) => (
                    <div key={clip.id} className="rounded-lg bg-black/40 ring-1 ring-white/10 p-1.5 space-y-1.5">
                      <div className="relative w-full rounded bg-black aspect-[9/16] overflow-hidden">
                        <video src={clip.url} muted playsInline preload="metadata" className="absolute inset-0 w-full h-full object-cover" />
                      </div>
                      <div className="flex items-center justify-between gap-1">
                        <span className="text-[11px] text-gray-400 truncate" title={clip.name}>{clip.name}</span>
                        <button onClick={() => deleteClip(clip)} className="text-gray-500 hover:text-red-400 shrink-0"><Trash2 size={13} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {err && <p className="text-sm text-red-400">{err}</p>}

            <button onClick={save} disabled={saving || !!busy}
              className="w-full flex items-center justify-center gap-2 bg-[#0000fe] hover:brightness-110 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-50">
              {saving && <Loader2 className="animate-spin" size={16} />}
              {existing ? 'Save changes' : 'Create preset'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function CreativesModal({ date, artistNameHint, onClose, onChanged }: {
  date: TourDate;
  artistNameHint: string;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [presets, setPresets] = useState<ArtistPreset[]>([]);
  const [artistKey, setArtistKey] = useState('');
  const [clips, setClips] = useState<BaseClip[]>([]);
  const [creatives, setCreatives] = useState<Creative[]>([]);
  const [busyClip, setBusyClip] = useState<string | null>(null);
  const [errByClip, setErrByClip] = useState<Record<string, string | null>>({});

  const dateText = `${MONTHS[new Date(date.date).getMonth()]} ${new Date(date.date).getDate()}, ${new Date(date.date).getFullYear()}`;

  useEffect(() => {
    db.from('tour_video_artists').select('artist_key, label').then(({ data }: any) => {
      const list: ArtistPreset[] = data || [];
      setPresets(list);
      const hint = (artistNameHint || '').toLowerCase();
      const match = list.find((p) => p.artist_key.toLowerCase() === hint || (p.label || '').toLowerCase() === hint);
      setArtistKey(match?.artist_key || list[0]?.artist_key || '');
    });
  }, []);

  useEffect(() => {
    if (!artistKey) { setClips([]); return; }
    db.from('tour_video_base_clips').select('id, name, artist_key, url, width, height').eq('artist_key', artistKey).order('sort')
      .then(({ data }: any) => setClips(data || []));
  }, [artistKey]);

  async function loadCreatives() {
    const { data } = await db.from('tour_date_video_creatives')
      .select('id, tour_date_id, url, thumbnail_url, label, base_clip_id')
      .eq('tour_date_id', date.id);
    setCreatives((data as Creative[]) || []);
  }
  useEffect(() => { loadCreatives(); }, [date.id]);

  const creativeByClip = useMemo(() => {
    const m: Record<string, Creative> = {};
    for (const c of creatives) if (c.base_clip_id) m[c.base_clip_id] = c;
    return m;
  }, [creatives]);
  const looseCreatives = creatives.filter((c) => !c.base_clip_id);

  async function deleteCreative(c: Creative) {
    if (!confirm('Delete this rendered video?')) return;
    const { error } = await db.from('tour_date_video_creatives').delete().eq('id', c.id);
    if (error) { toast.error(error.message); return; }
    try {
      const path = c.url.split('/videos/')[1];
      if (path) await supabase.storage.from('videos').remove([decodeURIComponent(path.split('?')[0])]);
    } catch { /* best effort */ }
    toast.success('Rendered video deleted');
    await loadCreatives();
    onChanged();
  }

  async function generate(clip: BaseClip) {
    setBusyClip(clip.id);
    setErrByClip((e) => ({ ...e, [clip.id]: null }));
    try {
      const data = await callFn('render-tour-video', {
        artistKey, baseClipId: clip.id, tourDateId: date.id,
        date: dateText, venue: date.venue || '', city: date.city || '',
      });
      if (data?.error) throw new Error(data.error);
      const renderId = data.renderId;
      for (let i = 0; i < 150; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        const s = await callFn('tour-video-status', { renderId });
        if (s?.status === 'success') {
          await db.from('tour_date_video_creatives').insert({
            tour_date_id: date.id, render_id: renderId, base_clip_id: clip.id,
            url: s.outputUrl, label: `${date.venue || ''} ${date.city || ''}`.trim(), source: 'rendered',
          });
          toast.success('Video rendered & attached');
          await loadCreatives();
          onChanged();
          return;
        }
        if (s?.status === 'failed') throw new Error(s.error || 'Render failed');
      }
      throw new Error('Timed out waiting for render');
    } catch (e: any) {
      setErrByClip((prev) => ({ ...prev, [clip.id]: e.message || String(e) }));
    } finally {
      setBusyClip(null);
    }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4" onClick={onClose}>
      <div className="w-full max-w-3xl max-h-[88vh] overflow-y-auto rounded-2xl bg-[#1a1a1a] p-6 ring-1 ring-white/10" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold">Creatives</h3>
            <p className="text-xs text-gray-500">{dateText} · {date.venue} {date.city ? `| ${date.city}` : ''}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={18} /></button>
        </div>

        {presets.length === 0 ? (
          <p className="text-sm text-amber-400">No render presets found. Seed tour_video_artists first.</p>
        ) : (
          <>
            {presets.length > 1 && (
              <div className="mb-4">
                <label className="block text-xs text-gray-400 mb-1">Style preset</label>
                <select value={artistKey} onChange={(e) => setArtistKey(e.target.value)}
                  className="w-full bg-[#111] rounded-lg px-3 py-2 text-sm ring-1 ring-white/10">
                  {presets.map((p) => <option key={p.artist_key} value={p.artist_key}>{p.label}</option>)}
                </select>
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {clips.map((clip) => {
                const c = creativeByClip[clip.id];
                const busy = busyClip === clip.id;
                const err = errByClip[clip.id];
                return (
                  <div key={clip.id} className="rounded-lg bg-black/40 ring-1 ring-white/10 p-2 space-y-2">
                    {c ? (
                      <video src={c.url} controls preload="metadata" poster={c.thumbnail_url || undefined}
                        className="w-full rounded bg-black aspect-[9/16] object-contain" />
                    ) : (
                      <div className="relative w-full rounded bg-black aspect-[9/16] overflow-hidden">
                        <video src={clip.url} muted playsInline preload="metadata"
                          className="absolute inset-0 w-full h-full object-cover opacity-50" />
                        <div className="absolute inset-0 grid place-items-center">
                          <button onClick={() => generate(clip)} disabled={busy || !!busyClip}
                            className="flex items-center gap-1.5 bg-[#0000fe] hover:brightness-110 disabled:opacity-50 px-3 py-1.5 rounded-lg text-xs font-semibold">
                            {busy ? <Loader2 className="animate-spin" size={14} /> : <Film size={14} />}
                            {busy ? 'Rendering…' : 'Generate'}
                          </button>
                        </div>
                      </div>
                    )}
                    <div className="flex items-center justify-between gap-2 text-xs text-gray-400">
                      <span className="truncate" title={clip.name}>{clip.name}</span>
                      {c && (
                        <div className="flex items-center gap-2 shrink-0">
                          <button onClick={() => deleteCreative(c)} title="Delete rendered video"
                            className="text-gray-500 hover:text-red-400">
                            <Trash2 size={12} />
                          </button>
                          <a href={downloadUrl(c)} className="flex items-center gap-1 text-[#5b5bff] hover:underline">
                            <Download size={12} /> Download
                          </a>
                        </div>
                      )}
                    </div>
                    {err && <p className="text-[11px] text-red-400">{err}</p>}
                  </div>
                );
              })}
              {clips.length === 0 && <p className="text-sm text-gray-500 col-span-full">No base clips for this preset.</p>}
            </div>

            {looseCreatives.length > 0 && (
              <>
                <p className="text-xs text-gray-500 mt-5 mb-2">Other creatives</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {looseCreatives.map((c) => (
                    <div key={c.id} className="rounded-lg bg-black/40 ring-1 ring-white/10 p-2 space-y-2">
                      <video src={c.url} controls preload="metadata" poster={c.thumbnail_url || undefined}
                        className="w-full rounded bg-black aspect-[9/16] object-contain" />
                      <div className="flex items-center justify-between gap-2 text-xs text-gray-400">
                        <span className="truncate" title={c.label || 'video'}>{c.label || 'video'}</span>
                        <div className="flex items-center gap-2 shrink-0">
                          <button onClick={() => deleteCreative(c)} title="Delete rendered video"
                            className="text-gray-500 hover:text-red-400">
                            <Trash2 size={12} />
                          </button>
                          <a href={downloadUrl(c)} className="flex items-center gap-1 text-[#5b5bff] hover:underline">
                            <Download size={12} /> Download
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function NumCell({ value, onSave }: { value: number | null; onSave: (v: number | null) => void }) {
  const [v, setV] = useState(value?.toString() ?? '');
  useEffect(() => setV(value?.toString() ?? ''), [value]);
  return (
    <input
      value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => onSave(v === '' ? null : Number(v))}
      inputMode="decimal"
      className="w-20 bg-transparent rounded px-2 py-1 text-xs ring-1 ring-white/10 focus:ring-[#0000fe] outline-none"
      placeholder="—"
    />
  );
}

function TextCell({ value, onSave, placeholder }: { value: string | null; onSave: (v: string | null) => void; placeholder?: string }) {
  const [v, setV] = useState(value ?? '');
  useEffect(() => setV(value ?? ''), [value]);
  return (
    <input
      value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => onSave(v === '' ? null : v)}
      className="w-24 bg-transparent rounded px-2 py-1 text-xs ring-1 ring-white/10 focus:ring-[#0000fe] outline-none"
      placeholder={placeholder}
    />
  );
}

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
